import * as React from "react";
import { useDirection } from "@radix-ui/react-direction";
import { Toaster as SonnerToaster, toast } from "sonner";

export type ToasterToastKind = "success" | "error" | "info" | "warning";

export interface ToasterToastMessage {
  readonly id?: string;
  readonly kind: ToasterToastKind;
  readonly title: string;
  readonly description?: string;
}

export type ToastSubscriber = (
  listener: (message: ToasterToastMessage) => void,
) => () => void;

export interface ToasterProps extends React.ComponentProps<
  typeof SonnerToaster
> {
  subscribe?: ToastSubscriber;
}

export function Toaster({ subscribe, dir, ...props }: ToasterProps) {
  // Sonner can't read DirectionProvider context on its own; must explicitly
  // pass dir to RTL-mirror the swipe gesture and icon spacing at runtime.
  const ambientDir = useDirection();

  React.useEffect(() => {
    if (!subscribe) return;
    return subscribe((message) => {
      const { kind, title, description, id } = message;
      const data: { id?: string | number; description?: string } = {};
      if (id !== undefined) {
        data.id = id;
      }
      if (description !== undefined) {
        data.description = description;
      }
      const toastFn = toast[kind];
      if (typeof toastFn === "function") {
        toastFn(title, data);
      } else {
        toast(title, data);
      }
    });
  }, [subscribe]);

  return (
    <SonnerToaster
      duration={3000}
      dir={dir ?? ambientDir}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-container-default-background group-[.toaster]:text-text group-[.toaster]:border-container-default-border group-[.toaster]:shadow-lg group-[.toaster]:rounded-md data-[type=error]:!bg-red-600 data-[type=error]:!text-white data-[type=error]:!border-red-700 data-[type=success]:!bg-green-600 data-[type=success]:!text-white data-[type=success]:!border-green-700",
          description:
            "text-text-placeholder text-body-s-r group-[[data-type=error]]:!text-white/90 group-[[data-type=success]]:!text-white/90",
          actionButton:
            "bg-primary-button-background text-primary-button-text",
          cancelButton:
            "bg-secondary-button-background text-secondary-button-text",
          title:
            "text-body-m-b group-[[data-type=error]]:!text-white group-[[data-type=success]]:!text-white",
          success:
            "!bg-green-600 !text-white !border-green-700 [&_[data-title]]:!text-white [&_[data-description]]:!text-white/90 [&_svg]:!text-white",
          error:
            "!bg-red-600 !text-white !border-red-700 [&_[data-title]]:!text-white [&_[data-description]]:!text-white/90 [&_svg]:!text-white",
        },
        ...props.toastOptions,
      }}
      {...props}
    />
  );
}

export { toast };
